import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fetchAutenticado, ErrorApi } from '../api/cliente';
import { Cargando, Error as AvisoError, Paginacion } from '../componentes/comunes';
import { useSesion } from '../sesion';
import { folioOT } from '../folio';

/**
 * Cerradas: lo que terminó y todavía no se puede cobrar.
 *
 * Karin: «actualmente las órdenes que se cierran se van directo a "por
 * facturar" […] no se pueden facturar todavía hasta que el mismo
 * facturación las pase. Lo que sucede es que primero y de forma manual
 * facturación tiene que revisar un sistema externo y revisar el estatus
 * de su orden, para poder definir si se factura o no».
 *
 * Esa consulta no vive en APRISA, así que el sistema no puede hacerla
 * solo. Lo que sí puede es dejar de mentir: antes, cerrar una orden la
 * declaraba cobrable y «Pendientes por facturar» mezclaba lo que se podía
 * cobrar con lo que faltaba confirmar. Ahora son dos listas, y la de allá
 * sólo trae lo que de verdad se puede emitir hoy.
 *
 * ── Dos caminos para lo mismo ───────────────────────────────────────
 *
 * El botón, para la orden que alguien acaba de consultar. Y el archivo,
 * para el día del cierre de mes, cuando son cien. Los dos hacen lo mismo
 * y dejan el mismo rastro en el seguimiento de cada orden: cuando alguien
 * pregunte por qué se emitió esta factura, la respuesta está ahí.
 */

interface Orden {
  id: string;
  folio: string;
  folioServicio: string | null;
  cliente: { nombre: string } | null;
  sucursal: { determinante: string; nombre: string } | null;
  fechaCierre: string | null;
  diasDesdeCierre: number | null;
  tieneCargoAparte: boolean;
  precioVenta: number | null;
  situacion: string;
}

interface Pagina {
  data: Orden[];
  total: number;
  pagina: number;
  limite: number;
}

const NOMBRE_RESULTADO: Record<string, string> = {
  pasa: 'Pasa a facturar',
  otro_estado: 'Sigue esperando',
  no_encontrada: 'No se encontró',
  no_esperaba: 'No estaba esperando',
  repetida: 'Repetida en el archivo',
};

interface Renglon {
  renglon: number;
  resultado: string;
  mensaje: string;
  folio: string;
  estado: string;
}

interface Resumen {
  total: number;
  pasan: number;
  otroEstado: number;
  noEncontradas: number;
  noEsperaban: number;
  repetidas: number;
}

interface Respuesta {
  resumen: Resumen;
  renglones: Renglon[];
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/** Sube el archivo. Va con fetch porque es multipart y no JSON. */
async function subir(archivo: File, aplicar: boolean): Promise<Respuesta> {
  const cuerpo = new FormData();
  cuerpo.append('archivo', archivo);

  const respuesta = await fetchAutenticado(
    aplicar
      ? '/api/v1/facturacion/cerradas/pasar'
      : '/api/v1/facturacion/cerradas/vista-previa',
    { method: 'POST', body: cuerpo },
  );

  const datos = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      String(datos.codigo_negocio ?? 'error'),
      String(datos.detail ?? 'No se pudo leer el archivo.'),
    );
  }

  return datos as Respuesta;
}

export default function Cerradas({ alPasar }: { alPasar?: () => void }) {
  const { puede } = useSesion();
  const entrada = useRef<HTMLInputElement>(null);
  const [pagina, setPagina] = useState<Pagina | null>(null);
  const [numero, setNumero] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [pasando, setPasando] = useState<string | null>(null);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Respuesta | null>(null);
  const [aplicado, setAplicado] = useState<Resumen | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const puedePasar = puede('facturacion', 'editar');

  const cargar = useCallback(() => {
    setCargando(true);
    api.get<Pagina>('/facturacion/cerradas', {
      busqueda: busqueda || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);

    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  function pasarUna(id: string) {
    setPasando(id);
    setError(null);
    api.post(`/facturacion/cerradas/${id}/pasar`, {})
      .then(() => { cargar(); alPasar?.(); })
      .catch(setError)
      .finally(() => setPasando(null));
  }

  function elegir(nuevo: File | null) {
    setArchivo(nuevo);
    setPrevia(null);
    setAplicado(null);
    setError(null);
    if (!nuevo) return;

    setTrabajando(true);
    subir(nuevo, false)
      .then(setPrevia)
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  function aplicar() {
    if (!archivo) return;

    setTrabajando(true);
    setError(null);
    subir(archivo, true)
      .then((r) => {
        setAplicado(r.resumen);
        setPrevia(r);
        cargar();
        alPasar?.();
      })
      .catch(setError)
      .finally(() => setTrabajando(false));
  }

  return (
    <>
      <div className="panel">
        <h2>Cerradas, esperando validación</h2>
        <p className="sub">
          El trabajo terminó, pero antes de cobrarlo hay que confirmar su
          estatus en el sistema del cliente. Lo que se pasa de aquí es lo
          que aparece en «Pendientes por facturar».
        </p>

        <AvisoError error={error} />

        <div className="controles">
          <input
            type="search"
            placeholder="Folio, orden de servicio o sucursal…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
        </div>

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">
                No hay ninguna orden esperando validación.
              </div>
            ) : (
              <>
                <table style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>Orden</th>
                      <th>Cliente</th>
                      <th>Sucursal</th>
                      <th>Cerrada</th>
                      <th className="numero">Días</th>
                      <th className="numero">Precio</th>
                      {puedePasar && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {pagina.data.map((o) => (
                      <tr key={o.id}>
                        <td className="mono">
                          {folioOT(o.folio)}
                          {o.folioServicio && (
                            <div className="conteo">OS {o.folioServicio}</div>
                          )}
                        </td>
                        <td>{o.cliente?.nombre ?? '—'}</td>
                        <td>
                          {o.sucursal
                            ? `${o.sucursal.determinante} — ${o.sucursal.nombre}`
                            : '—'}
                        </td>
                        <td>{o.fechaCierre ? fecha(o.fechaCierre) : '—'}</td>
                        {/* Lo que lleva esperando. Una orden que el
                            cliente ya validó hace semanas y nadie pasó es
                            dinero detenido por un clic que falta. */}
                        <td
                          className="numero"
                          style={(o.diasDesdeCierre ?? 0) > 30
                            ? { color: '#b02a37', fontWeight: 600 }
                            : undefined}
                        >
                          {o.diasDesdeCierre ?? '—'}
                        </td>
                        <td className="numero">
                          {o.precioVenta != null ? moneda(o.precioVenta) : '—'}
                        </td>
                        {puedePasar && (
                          <td>
                            <button
                              className="enlace"
                              disabled={pasando === o.id}
                              onClick={() => pasarUna(o.id)}
                            >
                              Pasar a por facturar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Paginacion
                  pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
                  onCambiar={setNumero}
                />
              </>
            )}
          </>
        )}
      </div>

      {puedePasar && (
        <div className="panel">
          <h2>Pasar varias con el archivo del cliente</h2>
          <p className="sub">
            El archivo que exporta el sistema del cliente. Se leen dos
            columnas —el Task ID y el estado de la orden de trabajo— y el
            resto se ignora. Pasan sólo las que están en «pendiente de
            validación final del proveedor»; las demás se listan con el
            estado que traen y no se tocan.
          </p>

          <div
            className="aviso"
            role="button"
            tabIndex={0}
            style={{ textAlign: 'center', cursor: 'pointer', padding: 24, marginTop: 12 }}
            onClick={() => entrada.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter') entrada.current?.click(); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              elegir(e.dataTransfer.files[0] ?? null);
            }}
          >
            {archivo
              ? <strong>{archivo.name}</strong>
              : <>Arrastra aquí el archivo de estatus, o haz clic para elegirlo.</>}
          </div>

          <input
            ref={entrada}
            type="file"
            accept=".xlsx,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={(e) => elegir(e.target.files?.[0] ?? null)}
          />

          {trabajando && <p className="sub">Leyendo el archivo…</p>}

          {previa && (
            <>
              <div className="rejilla-catalogos" style={{ marginTop: 16 }}>
                <Conteo titulo="Órdenes en el archivo" valor={previa.resumen.total} />
                <Conteo
                  titulo={aplicado ? 'Pasaron' : 'Van a pasar'}
                  valor={previa.resumen.pasan}
                />
                <Conteo titulo="Siguen esperando" valor={previa.resumen.otroEstado} />
                <Conteo
                  titulo="No se encontraron"
                  valor={previa.resumen.noEncontradas}
                  alerta={previa.resumen.noEncontradas > 0}
                />
              </div>

              {aplicado ? (
                <div className="aviso exito" style={{ marginTop: 14 }}>
                  Listo: {aplicado.pasan} órdenes ya se pueden facturar.
                  {aplicado.total - aplicado.pasan > 0 && (
                    <> Las otras {aplicado.total - aplicado.pasan} se quedaron
                    como estaban, con su motivo abajo.</>
                  )}
                </div>
              ) : (
                <div className="controles" style={{ marginTop: 14 }}>
                  <button
                    className="primario"
                    onClick={aplicar}
                    disabled={trabajando || previa.resumen.pasan === 0}
                  >
                    Pasar {previa.resumen.pasan} a por facturar
                  </button>
                  <button onClick={() => elegir(null)} disabled={trabajando}>
                    Descartar
                  </button>
                  {previa.resumen.pasan === 0 && (
                    <span className="conteo">
                      Ninguna orden de este archivo pasa todavía.
                    </span>
                  )}
                </div>
              )}

              <table style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th className="numero">Renglón</th>
                    <th>Orden</th>
                    <th>Estado del cliente</th>
                    <th>Resultado</th>
                    <th>Qué pasa</th>
                  </tr>
                </thead>
                <tbody>
                  {previa.renglones.map((r) => (
                    <tr key={`${r.renglon}-${r.folio}`}>
                      <td className="numero">{r.renglon}</td>
                      <td className="mono">{r.folio}</td>
                      <td className="conteo">{r.estado || '—'}</td>
                      <td>
                        <span className={r.resultado === 'pasa'
                          ? 'etiqueta si'
                          : r.resultado === 'otro_estado' ? undefined : 'etiqueta no'}
                        >
                          {NOMBRE_RESULTADO[r.resultado] ?? r.resultado}
                        </span>
                      </td>
                      <td className="conteo">{r.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </>
  );
}

function Conteo({ titulo, valor, alerta }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: alerta ? '#b02a37' : undefined }}>
        {valor}
      </div>
    </div>
  );
}
